// V1E5C1: logical writing only. No physical unit, Canon, approval, publication or Novel save.
import {normalizeManuscriptContent, plainTextFromManuscriptContent} from './childrensBookManuscriptContent';
const clean = value => typeof value === 'string' ? value.trim() : '';
const list = value => Array.isArray(value) ? value : [];
const reject = code => ({ok:false,code});
const empty = draft => list(draft?.bookMap?.units).length === 0 &&
  list(draft?.manuscriptPlan?.entries).length === 0;
export function sceneFirstEligibleV1E5C(draft = {}) {
  const version = draft?.manuscriptPlan?.chapterSceneOutline?.version;
  if (version === 2) return empty(draft);
  // Never implicitly migrate an existing page-authored v1 book or unknown outline.
  return version == null && empty(draft);
}
export function sceneFirstOutlineV1E5C(draft = {}) {
  if (!sceneFirstEligibleV1E5C(draft)) return null;
  const outline = draft?.manuscriptPlan?.chapterSceneOutline;
  if (outline?.version === 2 && Array.isArray(outline.chapters)) return outline;
  if (outline == null) return {version:2,chapters:[]};
  return null;
}
export function sceneFirstNavigationV1E5C(draft = {}) {
  const outline = sceneFirstOutlineV1E5C(draft);
  if (!outline) return reject('unsupported_saved_outline');
  const chapters = [], chapterIds = new Set(), sceneIds = new Set();
  for (const chapter of outline.chapters) {
    if (!clean(chapter?.id) || !clean(chapter?.title) || !Array.isArray(chapter.scenes) || chapterIds.has(chapter.id))
      return reject('invalid_chapter');
    chapterIds.add(chapter.id);
    const scenes=[];
    for (const scene of chapter.scenes) {
      if (!clean(scene?.id) || !clean(scene?.title) || !Array.isArray(scene.unitIds) || scene.unitIds.length || sceneIds.has(scene.id))
        return reject('invalid_scene');
      sceneIds.add(scene.id);
      scenes.push({key:scene.id,title:scene.title,entries:[]});
    }
    chapters.push({key:chapter.id,title:chapter.title,kind:'chapter',scenes});
  }
  return {ok:true,mode:'scene-first',sections:chapters,totalUnits:0};
}
export function addSceneFirstChapterV1E5C(draft, id, title) {
  const outline = sceneFirstOutlineV1E5C(draft);
  const nav = sceneFirstNavigationV1E5C(draft);
  if (!outline || !nav.ok) return reject('unsupported_saved_outline');
  if (!clean(id) || !clean(title) || id.length>120 || title.length>240 || outline.chapters.length>=250 ||
      outline.chapters.some(ch=>ch.id===id)) return reject('invalid_chapter_identity');
  return {ok:true,outline:{version:2,chapters:[...outline.chapters,{id,title,scenes:[]}]}};
}
export function addSceneFirstSceneV1E5C(draft, chapterId, id, title) {
  const outline=sceneFirstOutlineV1E5C(draft);
  const nav=sceneFirstNavigationV1E5C(draft);
  if (!outline || !nav.ok) return reject('unsupported_saved_outline');
  if (!clean(id) || !clean(title) || id.length>120 || title.length>240 ||
      !outline.chapters.some(ch=>ch.id===chapterId) ||
      outline.chapters.flatMap(ch=>ch.scenes).some(sc=>sc.id===id) ||
      outline.chapters.reduce((n,ch)=>n+ch.scenes.length,0)>=2000) return reject('invalid_scene_identity');
  return {ok:true,outline:{version:2,chapters:outline.chapters.map(ch=>ch.id===chapterId
    ? {...ch,scenes:[...ch.scenes,{id,title,unitIds:[]}]}:ch)}};
}
// V1E6A: existing Scene IDs and text are never changed by Chapter edits.
export function renameSceneFirstChapterV1E6A(draft, chapterId, title) {
  const outline=sceneFirstOutlineV1E5C(draft);
  const nav=sceneFirstNavigationV1E5C(draft);
  const trimmed=clean(title);
  if(!outline||!nav.ok||!clean(chapterId)||!trimmed||trimmed.length>240||
      !outline.chapters.some(ch=>ch.id===chapterId)) return reject('invalid_chapter_rename');
  return {ok:true,outline:{version:2,chapters:outline.chapters.map(ch=>ch.id===chapterId
    ? {...ch,title:trimmed}:ch)}};
}
export function moveSceneFirstChapterV1E6A(draft, chapterId, toIndex) {
  const outline=sceneFirstOutlineV1E5C(draft);
  const nav=sceneFirstNavigationV1E5C(draft);
  if(!outline||!nav.ok||!Number.isInteger(toIndex)||toIndex<0||
      toIndex>=outline.chapters.length) return reject('invalid_chapter_position');
  const from=outline.chapters.findIndex(ch=>ch.id===chapterId);
  if(from<0) return reject('unknown_chapter');
  const chapters=outline.chapters.slice();
  const [chapter]=chapters.splice(from,1);
  chapters.splice(toIndex,0,chapter);
  return {ok:true,outline:{version:2,chapters}};
}
export function sceneFirstDraftV1E5C(draft, sceneId) {
  const scene = list(draft?.manuscriptPlan?.sceneDrafts?.scenes).find(row=>row.sceneId===sceneId);
  return {id:sceneId,sceneId,manuscriptContent:scene?.manuscriptContent||{version:1,blocks:[]}};
}
export function changeSceneFirstTextV1E5C(draft, sceneId, manuscriptContent) {
  const nav=sceneFirstNavigationV1E5C(draft);
  if (!nav.ok || !nav.sections.some(ch=>ch.scenes.some(s=>s.key===sceneId))) return reject('unknown_scene');
  const normalized = normalizeManuscriptContent(manuscriptContent);
  if (!normalized.ok || !normalized.content) return reject('invalid_manuscript_content');
  const existing=list(draft?.manuscriptPlan?.sceneDrafts?.scenes);
  if (draft?.manuscriptPlan?.sceneDrafts && (draft.manuscriptPlan.sceneDrafts.version!==1 ||
      !Array.isArray(draft.manuscriptPlan.sceneDrafts.scenes))) return reject('unknown_scene_draft_schema');
  if (existing.some((s,i)=>s.sceneId===sceneId && existing.findIndex(z=>z.sceneId===sceneId)!==i)) return reject('duplicate_scene_draft');
  const seen=existing.some(x=>x.sceneId===sceneId);
  return {ok:true,sceneDrafts:{version:1,scenes:seen
    ? existing.map(x=>x.sceneId===sceneId ? {sceneId,manuscriptContent:normalized.content}:x)
    : [...existing,{sceneId,manuscriptContent:normalized.content}]}};
}
export function sceneFirstNovelPresentationV1E5C({draft,navigation}={}) {
  if (navigation?.ok!==true || navigation.mode!=='scene-first') return reject('invalid_navigation');
  const chapters=navigation.sections;
  const sceneMap=new Map();
  for (const row of list(draft?.manuscriptPlan?.sceneDrafts?.scenes)) {
    if (!clean(row?.sceneId) || sceneMap.has(row.sceneId)) return reject('duplicate_scene_draft');
    sceneMap.set(row.sceneId,row.manuscriptContent);
  }
  const scenes=[]; const chapterProxies=[];
  for (const ch of chapters) {
    const sceneIds=[];
    for(const sc of ch.scenes){
      sceneIds.push(sc.key);
      const content=sceneMap.get(sc.key);
      scenes.push({_id:sc.key,title:sc.title,text:content?plainTextFromManuscriptContent(content):'',
        // Novel structure-preview display filter only; not a published/adopted Scene.
        type:'canon',status:'approved'});
    }
    chapterProxies.push({id:ch.key,title:ch.title,sceneIds});
  }
  if ([...sceneMap.keys()].some(id=>!scenes.some(s=>s._id===id))) return reject('orphan_scene_draft');
  const title=clean(draft?.identity?.title)||'Untitled Children’s Book';
  const project={id:'childrens-book-presentation-proxy-only',projectId:'childrens-book-presentation-proxy-only',
    title,isStructurePreview:true,childrensBookPresentationBridgeV1E4C:true,
    structurePreviewStory:{_id:'childrens-book-presentation-proxy-only',title,writingMode:'solo',
      publicationStatus:'inDevelopment',scenes,frontMatter:[],backMatter:[]},
    structurePreviewChapters:chapterProxies};
  return {ok:true,project,chapters:chapterProxies,sceneCount:scenes.length,physicalUnitCount:0};
}
